import React, { useCallback, useEffect, useRef, useState } from "react";
import { FormHelperText, TextField, Typography } from "@mui/material";
import DocumentUpload from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useTranslation } from "react-i18next";
import { getUploadEntryName, getUploadEntryUrl } from "../attachmentUtils";
import { MAX_TEXT_LENGTH, resumeInterruptionSchema } from "./resumeInterruptionSchema";

interface ResumeVesselCallDrawerProps {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  onDocumentUpload?: (file: File) => void;
  onSubmit?: (payload?: any) => void;
}

const DRAWER_NAME = "resumeVesselCallDrawer";
const LANG_BASE = "vessel-call";

const ResumeVesselCallDrawer: React.FC<ResumeVesselCallDrawerProps> = ({
  isOpen,
  onClose,
  onDocumentUpload,
  onSubmit,
}) => {
  const { t } = useTranslation();

  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const charCounterSx = {
    ml: 0,
    mt: 0.5,
    fontSize: 12,
    textAlign: "right",
    color: "text.secondary",
  };

  const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
  const uploadSignatureRef = useRef<string | null>(null);
  const [resolutionDetails, setResolutionDetails] = useState("");
  const [errors, setErrors] = useState<{ resolutionDetails?: string; internalFile?: string }>({});

  const isPrintableKey = (e: React.KeyboardEvent) =>
    e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

  const resolutionOnlySchema = resumeInterruptionSchema(t).pick({ resolutionDetails: true });
  const fileProfiles = useFileUploadProfiles();
  const internalProfile = {
    ...fileProfiles.sideDrawerApprovalFile,
    maxSize: MAX_FILE_SIZE_BYTES,
  };

  const internalFiles = useUploadStore(
    (s) => s.filesByKey.resumeInterruptionInternalAttachment ?? EMPTY_UPLOAD_ENTRIES,
  );
  const setFilesForKey = useUploadStore((s) => s.setFilesForKey);
  const clearFilesForKey = useUploadStore((s) => s.clearFilesForKey);

  const allowedInternalExtensions = internalProfile.format.map((ext: string) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const clearAll = useCallback(() => {
    clearFilesForKey("resumeInterruptionInternalAttachment");
    setResolutionDetails("");
    setErrors({});
    uploadSignatureRef.current = null;
  }, [clearFilesForKey]);

  useEffect(() => {
    if (isOpen) {
      clearAll();
    }
  }, [clearAll, isOpen]);

  const handleClose = () => {
    onClose(DRAWER_NAME);
  };

  const handleCancel = () => {
    clearAll();
    onClose(DRAWER_NAME);
  };

  const setInternalAttachment = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("resumeInterruptionInternalAttachment");
        return;
      }
      setFilesForKey("resumeInterruptionInternalAttachment", files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const getFileSignature = (entry: UploadEntry | undefined) => {
    if (!(entry instanceof File)) return null;
    return `${entry.name}-${entry.lastModified}-${entry.size}`;
  };

  const triggerUpload = useCallback(
    (
      entry: UploadEntry | undefined,
      handler: ((file: File) => void) | undefined,
      tracker: React.MutableRefObject<string | null>,
    ) => {
      if (!handler) return;
      if (!(entry instanceof File)) return;

      const signature = getFileSignature(entry);
      if (!signature) return;
      if (tracker.current === signature) return;

      tracker.current = signature;
      handler(entry);
    },
    [],
  );

  const handleFileUpload = (newFiles: File[]) => {
    if (newFiles.length > 1) {
      setErrors((prev) => ({ ...prev, internalFile: t("upload.one-document") }));
      return;
    }

    const file = newFiles[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setErrors((prev) => ({
        ...prev,
        internalFile: t("form-validation.max-file-size", {
          max: "10MB",
        }),
      }));
      return;
    }

    setErrors((prev) => ({ ...prev, internalFile: undefined }));
    setInternalAttachment([file]);
    triggerUpload(file, onDocumentUpload, uploadSignatureRef);
  };

  const removeInternalFile = () => {
    clearFilesForKey("resumeInterruptionInternalAttachment");
    uploadSignatureRef.current = null;
  };

  const handleSubmit = () => {
    const toValidate = {
      resolutionDetails,
      internalAttachments: internalFiles[0],
    };

    const validation = resumeInterruptionSchema(t).safeParse(toValidate);
    if (!validation.success) {
      const flat = validation.error.flatten().fieldErrors;
      setErrors((prev) => ({ ...prev, resolutionDetails: flat.resolutionDetails?.[0] }));
      return;
    }

    const payload = {
      status: "RESUMED",
      externalNotes: resolutionDetails,
      externalAttachmentsUrl: getUploadEntryUrl(internalFiles[0]),
      externalAttachmentsName: getUploadEntryName(internalFiles[0]) || undefined,
    };

    onSubmit?.(payload);
    clearAll();
    onClose(DRAWER_NAME);
  };

  return (
    <div data-testid="resume-interruption-vessel-call" className="min-w-[400px]">
      <SideDrawer
        isOpen={isOpen}
        title={t(`${LANG_BASE}.resume.title`)}
        onClose={handleClose}
        onCancel={handleCancel}
        submitLabel={t(`${LANG_BASE}.resume.submit-button`)}
        onSubmit={handleSubmit}
      >
        <Typography
          variant="body1"
          data-testid="resume-interruption-vessel-call-subtitle"
          sx={{ color: "text.primary", px: 3, pb: 3 }}
          className="whitespace-pre-line break-words"
        >
          {t(`${LANG_BASE}.resume.subtitle`)}
        </Typography>

        <div className="flex-1 w-[400px] py-2">
          <div className="flex flex-col gap-4 px-6">
            <div className="flex flex-col gap-2" data-testid="resume-resolution-details">
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t(`${LANG_BASE}.resume.resolution-details`)}
              </Typography>
              <TextField
                multiline
                minRows={4}
                value={resolutionDetails}
                placeholder={t(`${LANG_BASE}.resume.resolution-details`)}
                sx={{ "& textarea": { padding: "12px", borderRadius: "8px" } }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (!isPrintableKey(e)) return;
                  if (resolutionDetails.length >= MAX_TEXT_LENGTH) {
                    setErrors((prev) => ({
                      ...prev,
                      resolutionDetails: t("form-validation.max-length", {
                        label: t(`${LANG_BASE}.resume.resolution-details`),
                        max: MAX_TEXT_LENGTH,
                      }),
                    }));
                  }
                }}
                onBlur={() => {
                  const res = resolutionOnlySchema.safeParse({ resolutionDetails });
                  if (!res.success) {
                    const flat = res.error.flatten().fieldErrors;
                    setErrors((prev) => ({
                      ...prev,
                      resolutionDetails: flat.resolutionDetails?.[0],
                    }));
                  } else {
                    setErrors((prev) => ({ ...prev, resolutionDetails: undefined }));
                  }
                }}
                onChange={(e) => {
                  const val = e.target.value;
                  setResolutionDetails(val);

                  const res = resolutionOnlySchema.safeParse({ resolutionDetails: val });
                  if (!res.success) {
                    const flat = res.error.flatten().fieldErrors;
                    setErrors((prev) => ({
                      ...prev,
                      resolutionDetails: flat.resolutionDetails?.[0],
                    }));
                  } else {
                    setErrors((prev) => ({ ...prev, resolutionDetails: undefined }));
                  }
                }}
              />
              {errors.resolutionDetails && (
                <FormHelperText sx={{ color: "error.main", m: 0 }}>
                  {errors.resolutionDetails}
                </FormHelperText>
              )}
              <FormHelperText sx={charCounterSx}>
                {resolutionDetails.length}/{MAX_TEXT_LENGTH}
              </FormHelperText>
            </div>

            <div className="flex flex-col gap-2" data-testid="resume-internal-attachments">
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {t(`${LANG_BASE}.common.attachments`)}
              </Typography>
              <DocumentUpload
                testId="resume-interruption-internal-attachment-upload"
                onUpload={handleFileUpload}
                name="resumeInterruptionInternalAttachment"
                conditions={internalProfile as any}
                files={internalFiles}
                setFiles={(next) => setFilesForKey("resumeInterruptionInternalAttachment", next)}
                maxFiles={1}
                allowedExtensions={allowedInternalExtensions}
                uploadDescription={t("upload.dragOrDropDescription")}
              />

              {errors.internalFile && (
                <FormHelperText sx={{ color: "error.main", m: 0 }}>
                  {errors.internalFile}
                </FormHelperText>
              )}

              {internalFiles[0] && (
                <SingleFile
                  file={internalFiles[0]}
                  testId="resume-interruption-internal-attachment"
                  fieldName="resumeInterruptionInternalAttachment"
                  currentFileIndex={0}
                  setFiles={() => {
                    setErrors((prev) => ({ ...prev, internalFile: undefined }));
                    setInternalAttachment([]);
                    uploadSignatureRef.current = null;
                  }}
                  removeFn={removeInternalFile}
                />
              )}
            </div>
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default ResumeVesselCallDrawer;
